import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// ─────────────────────────────────────────────────────────────
// Route: /delete-account
// Informational page explaining the account deletion process,
// data retention policy, and recovery window.
// Linked from: Profile → Danger Zone, Privacy Policy, App Store listing.
// ─────────────────────────────────────────────────────────────

// ── Pure-CSS graphic: Vault / Shield icon ────────────────────
const VaultGraphic = () => (
  <div className="da-vault">
    <div className="da-vault-body">
      <div className="da-vault-ring da-vault-ring--1" />
      <div className="da-vault-ring da-vault-ring--2" />
      <div className="da-vault-ring da-vault-ring--3" />
      <div className="da-vault-core">
        <div className="da-vault-line da-vault-line--h" />
        <div className="da-vault-line da-vault-line--v" />
        <div className="da-vault-dot" />
      </div>
    </div>
    <div className="da-vault-label">Data Vault</div>
  </div>
);

// ── Pure-CSS graphic: Timeline ────────────────────────────────
const TimelineGraphic = () => {
  const milestones = [
    { day: 'Day 0',  label: 'Request\nReceived',  sub: 'Account locked.\nPII cleared.',    active: true  },
    { day: 'Day 1',  label: 'Confirmation\nEmail', sub: 'Deletion notice\nsent to you.',   active: false },
    { day: 'Day 15', label: 'Recovery\nWindow',    sub: 'Contact support\nto restore.',    active: false },
    { day: 'Day 30', label: 'Permanent\nWipe',     sub: 'All PII erased\nfrom servers.',   active: false },
  ];

  return (
    <div className="da-timeline">
      <div className="da-timeline-track">
        <div className="da-timeline-fill" />
      </div>
      <div className="da-timeline-nodes">
        {milestones.map((m, i) => (
          <div key={i} className={`da-tnode ${m.active ? 'da-tnode--active' : ''}`}>
            <div className="da-tnode-dot" />
            <div className="da-tnode-day">{m.day}</div>
            <div className="da-tnode-label">{m.label}</div>
            <div className="da-tnode-sub">{m.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Pure-CSS graphic: Data split diagram ─────────────────────
const DataSplitGraphic = () => (
  <div className="da-split-graphic">
    <div className="da-split-col da-split-col--erased">
      <div className="da-split-icon">
        {[...Array(6)].map((_, i) => (
          <div key={i} className={`da-split-block da-split-block--erased da-split-block--${i}`} />
        ))}
      </div>
      <span className="da-split-col-label">Erased</span>
    </div>
    <div className="da-split-divider">
      <div className="da-split-divider-line" />
      <div className="da-split-divider-circle">VS</div>
      <div className="da-split-divider-line" />
    </div>
    <div className="da-split-col da-split-col--retained">
      <div className="da-split-icon">
        {[...Array(6)].map((_, i) => (
          <div key={i} className={`da-split-block da-split-block--retained da-split-block--${i}`} />
        ))}
      </div>
      <span className="da-split-col-label">Retained</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

const DeleteAccountPage = () => {
  const navigate  = useNavigate();
  const { user }  = useSelector((s) => s.auth);
  const [visible, setVisible] = useState({});
  const refs = useRef({});

  // Scroll-reveal
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            setVisible(v => ({ ...v, [e.target.dataset.reveal]: true }));
          }
        });
      },
      { threshold: 0.12 }
    );
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const ref = (key) => (el) => { refs.current[key] = el; };

  const handleDeleteCTA = () => {
    if (user) {
      navigate('/profile?tab=danger');
    } else {
      navigate('/login?redirect=/profile');
    }
  };

  const erased   = ['Name & email address', 'Mobile number', 'Saved delivery addresses', 'Login credentials', 'Password hash', 'Session tokens'];
  const retained = ['Order history', 'Purchase invoices', 'Payment transaction IDs', 'Shipment records', 'Refund history'];
  const commitments = [
    { num: '01', title: 'No Third-Party Sales', body: 'Your data is never sold, rented, or traded to advertisers, data brokers, or any external parties — ever.' },
    { num: '02', title: 'Minimal Collection', body: 'We only collect what\'s needed to fulfil your order and provide support. No hidden tracking profiles.' },
    { num: '03', title: 'Encrypted At Rest', body: 'All personal data stored on our servers is encrypted. Passwords are hashed with bcrypt — never stored in plain text.' },
    { num: '04', title: 'Legal Compliance Only', body: 'Business records retained post-deletion exist solely for accounting, fraud prevention, and legal compliance — nothing more.' },
    { num: '05', title: '30-Day Recovery Window', body: 'Deletion isn\'t instant. You have 30 days to change your mind and recover your account by contacting support.' },
    { num: '06', title: 'Full Anonymisation', body: 'After 30 days, your user record is anonymised — replaced with a placeholder so order foreign keys resolve without exposing your identity.' },
  ];

  return (
    <>
      <style>{daStyles}</style>

      <div className="da-root">

        {/* ══════════════════════════════════════════════════
            HERO
        ══════════════════════════════════════════════════ */}
        <section className="da-hero">
          <div className="da-hero-bg">
            {/* Decorative grid lines */}
            {[...Array(8)].map((_, i) => (
              <div key={i} className="da-hero-grid-line" style={{ left: `${i * 14.28}%` }} />
            ))}
            <div className="da-hero-grid-line da-hero-grid-line--h" style={{ top: '33%' }} />
            <div className="da-hero-grid-line da-hero-grid-line--h" style={{ top: '66%' }} />
          </div>

          <div className="da-hero-content">
            <span className="da-hero-eyebrow">Account Management</span>
            <h1 className="da-hero-title">
              Delete<br />
              <span className="da-hero-title-bold">Account</span>
            </h1>
            <p className="da-hero-sub">
              Everything you need to know before you go.
            </p>
            <div className="da-hero-tags">
              <span className="da-tag">30-Day Recovery</span>
              <span className="da-tag">Data Safety</span>
              <span className="da-tag">Legal Compliance</span>
            </div>
          </div>

          {/* Hero graphic — abstract circles */}
          <div className="da-hero-graphic">
            <div className="da-hero-circle da-hero-circle--1" />
            <div className="da-hero-circle da-hero-circle--2" />
            <div className="da-hero-circle da-hero-circle--3" />
            <div className="da-hero-circle-label">30<span>days</span></div>
          </div>

          <div className="da-hero-scroll-hint">
            <div className="da-hero-scroll-line" />
            <span>Scroll</span>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            WHAT HAPPENS — TIMELINE
        ══════════════════════════════════════════════════ */}
        <section
          className={`da-section da-section--dark ${visible['timeline'] ? 'da-reveal' : ''}`}
          data-reveal="timeline"
          ref={ref('timeline')}
        >
          <div className="da-section-inner">
            <div className="da-section-head">
              <span className="da-eyebrow da-eyebrow--light">The Process</span>
              <h2 className="da-section-title da-section-title--light">
                What Happens<br /><span className="da-bold">After You Request</span>
              </h2>
              <p className="da-section-desc da-section-desc--light">
                Deletion is not immediate. We enforce a 30-day grace period — both to protect
                you from accidental deletion and to give us time to process legal obligations.
              </p>
            </div>
            <TimelineGraphic />
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            WHAT STAYS / WHAT GOES
        ══════════════════════════════════════════════════ */}
        <section
          className={`da-section ${visible['split'] ? 'da-reveal' : ''}`}
          data-reveal="split"
          ref={ref('split')}
        >
          <div className="da-section-inner">
            <div className="da-section-head">
              <span className="da-eyebrow">Data Fate</span>
              <h2 className="da-section-title">
                What Gets Erased,<br /><span className="da-bold">What Stays</span>
              </h2>
              <p className="da-section-desc">
                We erase your identity. We keep the paper trail. This is the law — and it protects
                you too: retained records are your proof of purchase and your fraud protection.
              </p>
            </div>

            <DataSplitGraphic />

            <div className="da-data-grid">
              {/* Erased */}
              <div className="da-data-col da-data-col--erased">
                <div className="da-data-col-header">
                  <span className="da-data-col-badge da-data-col-badge--erased">Erased within 30 days</span>
                  <h3 className="da-data-col-title">Your Identity</h3>
                  <p className="da-data-col-sub">Permanently removed from our servers after the wipe date.</p>
                </div>
                <ul className="da-data-list">
                  {erased.map((item, i) => (
                    <li key={i} className="da-data-item da-data-item--erased">
                      <span className="da-data-item-icon">✕</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Retained */}
              <div className="da-data-col da-data-col--retained">
                <div className="da-data-col-header">
                  <span className="da-data-col-badge da-data-col-badge--retained">Retained indefinitely</span>
                  <h3 className="da-data-col-title">Business Records</h3>
                  <p className="da-data-col-sub">Required by law for accounting, tax, and fraud compliance.</p>
                </div>
                <ul className="da-data-list">
                  {retained.map((item, i) => (
                    <li key={i} className="da-data-item da-data-item--retained">
                      <span className="da-data-item-icon">◼</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="da-data-col-note">
                  <span className="da-data-col-note-icon">§</span>
                  <p>All retained records are anonymised — your name and email are replaced with a placeholder. The records exist; your identity does not.</p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            VAULT — DATA SAFETY COMMITMENTS
        ══════════════════════════════════════════════════ */}
        <section
          className={`da-section da-section--offwhite ${visible['vault'] ? 'da-reveal' : ''}`}
          data-reveal="vault"
          ref={ref('vault')}
        >
          <div className="da-section-inner">
            <div className="da-vault-layout">
              <div className="da-vault-left">
                <span className="da-eyebrow">Our Commitment</span>
                <h2 className="da-section-title">
                  How We Handle<br /><span className="da-bold">Your Data</span>
                </h2>
                <p className="da-section-desc">
                  Whether you stay or leave, these principles govern every byte
                  of data that passes through VESHH-EON.
                </p>
                <VaultGraphic />
              </div>
              <div className="da-vault-right">
                {commitments.map((c, i) => (
                  <div key={i} className="da-commitment" style={{ animationDelay: `${i * 0.07}s` }}>
                    <span className="da-commitment-num">{c.num}</span>
                    <div className="da-commitment-body">
                      <h4 className="da-commitment-title">{c.title}</h4>
                      <p className="da-commitment-desc">{c.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            RECOVERY WINDOW CALLOUT
        ══════════════════════════════════════════════════ */}
        <section
          className={`da-section da-section--dark ${visible['recovery'] ? 'da-reveal' : ''}`}
          data-reveal="recovery"
          ref={ref('recovery')}
        >
          <div className="da-section-inner da-section-inner--narrow">
            <div className="da-recovery-block">
              <div className="da-recovery-number">30</div>
              <div className="da-recovery-content">
                <span className="da-eyebrow da-eyebrow--light">Recovery Window</span>
                <h2 className="da-section-title da-section-title--light">
                  Changed Your Mind?<br /><span className="da-bold">You Have Time.</span>
                </h2>
                <p className="da-section-desc da-section-desc--light">
                  Deletion is reversible for 30 days. Visit the recovery page or email us at{' '}
                  <a href="mailto:support@veshheon.in" className="da-inline-link">support@veshheon.in</a>{' '}
                  with the subject line <em>"Account Recovery"</em> and we'll restore your account
                  — no questions asked.
                </p>
                <Link to="/recover-account" className="da-recovery-cta">
                  Recover Account →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            FAQ
        ══════════════════════════════════════════════════ */}
        <section
          className={`da-section ${visible['faq'] ? 'da-reveal' : ''}`}
          data-reveal="faq"
          ref={ref('faq')}
        >
          <div className="da-section-inner da-section-inner--narrow">
            <div className="da-section-head">
              <span className="da-eyebrow">Questions</span>
              <h2 className="da-section-title">
                Common <span className="da-bold">Questions</span>
              </h2>
            </div>
            <div className="da-faq-list">
              {[
                {
                  q: 'Can I log back in after requesting deletion?',
                  a: 'No. Once you confirm deletion, your account is locked immediately. You cannot sign in during the 30-day window — but you can recover the account by visiting the recovery page.'
                },
                {
                  q: 'Will my order history disappear?',
                  a: 'Your orders, invoices, and transaction records are retained for legal and accounting compliance. They exist as anonymised records — your name and contact info are removed, but the order data itself stays.'
                },
                {
                  q: 'What happens to my saved addresses?',
                  a: 'Saved addresses are cleared immediately when you confirm deletion. If you recover your account, you\'ll need to re-add them from your profile.'
                },
                {
                  q: 'Can I create a new account with the same email after deletion?',
                  a: 'After the 30-day permanent wipe, your email is anonymised to a placeholder in our system. You can register a new account with the same email after that point.'
                },
                {
                  q: 'Is this compliant with DPDP Act (India)?',
                  a: 'Yes. Our deletion and retention policy is designed in accordance with India\'s Digital Personal Data Protection Act, 2023 — we erase personal data upon request while retaining legally required business records.'
                },
                {
                  q: 'Who do I contact if something goes wrong?',
                  a: 'Email support@veshheon.in. For urgent account issues, use the subject line "URGENT: Account" and we\'ll prioritise your request.'
                },
              ].map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            FINAL CTA
        ══════════════════════════════════════════════════ */}
        <section
          className={`da-section da-section--cta ${visible['cta'] ? 'da-reveal' : ''}`}
          data-reveal="cta"
          ref={ref('cta')}
        >
          <div className="da-section-inner da-section-inner--narrow da-cta-block">
            <div className="da-cta-lines">
              <div className="da-cta-line" />
              <div className="da-cta-line da-cta-line--2" />
            </div>
            <span className="da-eyebrow">Ready to proceed?</span>
            <h2 className="da-section-title">
              Delete Your <span className="da-bold">Account</span>
            </h2>
            <p className="da-section-desc">
              This will take you to your profile's Danger Zone where you can confirm deletion.
              You'll need to type <strong>DELETE</strong> to confirm — no accidental clicks.
            </p>
            <div className="da-cta-actions">
              <button className="da-cta-btn-primary" onClick={handleDeleteCTA}>
                {user ? 'Go to Danger Zone' : 'Sign In to Delete'}
              </button>
              <Link to="/" className="da-cta-btn-secondary">
                Actually, I'll Stay
              </Link>
            </div>
            <p className="da-cta-note">
              Need help instead?{' '}
              <a href="mailto:support@veshheon.in" className="da-inline-link">Contact Support</a>
            </p>
          </div>
        </section>

      </div>
    </>
  );
};

// ── Accordion FAQ Item ────────────────────────────────────────
const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`da-faq-item ${open ? 'da-faq-item--open' : ''}`}>
      <button className="da-faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <span className="da-faq-icon">{open ? '−' : '+'}</span>
      </button>
      <div className="da-faq-a" style={{ maxHeight: open ? '200px' : '0' }}>
        <p>{a}</p>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────
   STYLES
──────────────────────────────────────────────────────────── */
const daStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600;700;900&display=swap');

  /* ── Root ── */
  .da-root {
    font-family: 'Exo 2', sans-serif;
    background: #f8f8f8;
    overflow-x: hidden;
  }

  /* ── Scroll reveal ── */
  .da-section { opacity: 0; transform: translateY(32px); transition: opacity 0.7s ease, transform 0.7s ease; }
  .da-reveal  { opacity: 1; transform: translateY(0); }
  .da-hero    { opacity: 1; transform: none; }

  /* ── Section base ── */
  .da-section { padding: 96px 80px; }
  .da-section--dark     { background: #000; }
  .da-section--offwhite { background: #f0f0f0; }
  .da-section--cta      { background: #f8f8f8; }
  .da-section-inner { max-width: 1200px; margin: 0 auto; }
  .da-section-inner--narrow { max-width: 800px; }

  .da-section-head { margin-bottom: 56px; }
  .da-section-title {
    font-size: clamp(28px, 4vw, 52px); font-weight: 200;
    letter-spacing: 0.06em; text-transform: uppercase;
    color: #000; line-height: 1.1; margin: 0 0 16px;
  }
  .da-section-title--light { color: #fff; }
  .da-bold { font-weight: 700; }
  .da-section-desc {
    font-size: 13px; font-weight: 300; letter-spacing: 0.05em;
    color: rgba(0,0,0,0.55); line-height: 1.8; max-width: 560px; margin: 0;
  }
  .da-section-desc--light { color: rgba(255,255,255,0.5); }

  /* ── Eyebrow ── */
  .da-eyebrow {
    display: flex; align-items: center; gap: 10px;
    font-size: 8px; font-weight: 400; letter-spacing: 0.5em;
    text-transform: uppercase; color: rgba(0,0,0,0.5); margin-bottom: 14px;
  }
  .da-eyebrow::before {
    content: ''; width: 20px; height: 1px;
    background: currentColor; display: block; flex-shrink: 0; opacity: 0.4;
  }
  .da-eyebrow--light { color: rgba(255,255,255,0.4); }

  /* ── Inline link ── */
  .da-inline-link { color: inherit; text-decoration: underline; text-underline-offset: 2px; }

  /* ─────────────────────────────────────────────────────
     HERO
  ───────────────────────────────────────────────────── */
  .da-hero {
    position: relative; min-height: 100vh;
    background: #000; display: flex;
    align-items: center; overflow: hidden;
    padding: 96px 80px;
  }

  /* Grid background lines */
  .da-hero-bg { position: absolute; inset: 0; z-index: 0; }
  .da-hero-grid-line {
    position: absolute; top: 0; bottom: 0; width: 1px;
    background: rgba(255,255,255,0.04);
  }
  .da-hero-grid-line--h {
    left: 0; right: 0; width: auto; height: 1px;
    top: auto; bottom: auto;
    background: rgba(255,255,255,0.04);
  }

  /* Hero content */
  .da-hero-content {
    position: relative; z-index: 2;
    max-width: 1200px; margin: 0 auto; width: 100%;
    flex: 1;
  }
  .da-hero-eyebrow {
    display: flex; align-items: center; gap: 12px;
    font-size: 8px; font-weight: 400; letter-spacing: 0.55em;
    text-transform: uppercase; color: rgba(255,255,255,0.35);
    margin-bottom: 20px;
  }
  .da-hero-eyebrow::before {
    content: ''; width: 24px; height: 1px;
    background: rgba(255,255,255,0.2); display: block; flex-shrink: 0;
  }
  .da-hero-title {
    font-size: clamp(60px, 10vw, 130px); font-weight: 200;
    letter-spacing: 0.04em; text-transform: uppercase;
    color: #fff; line-height: 0.9; margin: 0 0 32px;
  }
  .da-hero-title-bold { font-weight: 900; }
  .da-hero-sub {
    font-size: 13px; font-weight: 300; letter-spacing: 0.1em;
    text-transform: uppercase; color: rgba(255,255,255,0.4);
    margin: 0 0 32px;
  }
  .da-hero-tags { display: flex; gap: 10px; flex-wrap: wrap; }
  .da-tag {
    font-size: 7px; font-weight: 600; letter-spacing: 0.35em;
    text-transform: uppercase; color: rgba(255,255,255,0.5);
    border: 1px solid rgba(255,255,255,0.12); padding: 5px 12px;
  }

  /* Hero graphic */
  .da-hero-graphic {
    position: absolute; right: 80px; top: 50%;
    transform: translateY(-50%); z-index: 1;
    width: 300px; height: 300px;
    display: flex; align-items: center; justify-content: center;
  }
  .da-hero-circle {
    position: absolute; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.08);
    animation: da-rotate 20s linear infinite;
  }
  .da-hero-circle--1 { width: 300px; height: 300px; animation-duration: 30s; }
  .da-hero-circle--2 {
    width: 220px; height: 220px;
    border-color: rgba(255,255,255,0.05);
    animation-duration: 20s; animation-direction: reverse;
  }
  .da-hero-circle--2::after {
    content: ''; position: absolute; top: -4px; left: 50%;
    width: 8px; height: 8px; border-radius: 50%;
    background: rgba(255,255,255,0.3); transform: translateX(-50%);
  }
  .da-hero-circle--3 {
    width: 140px; height: 140px;
    border-color: rgba(255,255,255,0.1);
    animation-duration: 12s;
  }
  .da-hero-circle-label {
    position: relative; z-index: 2; text-align: center;
    font-size: 48px; font-weight: 200; color: rgba(255,255,255,0.15);
    letter-spacing: 0.02em; line-height: 1;
  }
  .da-hero-circle-label span {
    display: block; font-size: 10px; font-weight: 400;
    letter-spacing: 0.45em; text-transform: uppercase;
    color: rgba(255,255,255,0.2); margin-top: 4px;
  }
  @keyframes da-rotate { to { transform: rotate(360deg); } }

  /* Scroll hint */
  .da-hero-scroll-hint {
    position: absolute; bottom: 40px; left: 80px;
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    z-index: 2;
  }
  .da-hero-scroll-line {
    width: 1px; height: 48px;
    background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.3));
    animation: da-scroll-drop 2s ease-in-out infinite;
  }
  .da-hero-scroll-hint span {
    font-size: 7px; font-weight: 400; letter-spacing: 0.45em;
    text-transform: uppercase; color: rgba(255,255,255,0.2);
  }
  @keyframes da-scroll-drop {
    0%, 100% { transform: scaleY(1); opacity: 1; }
    50% { transform: scaleY(0.6); opacity: 0.5; }
  }

  /* ─────────────────────────────────────────────────────
     TIMELINE GRAPHIC
  ───────────────────────────────────────────────────── */
  .da-timeline { margin-top: 48px; position: relative; }
  .da-timeline-track {
    position: absolute; top: 16px; left: 0; right: 0;
    height: 1px; background: rgba(255,255,255,0.1);
  }
  .da-timeline-fill {
    height: 100%; width: 100%;
    background: linear-gradient(to right, rgba(255,255,255,0.5), rgba(255,255,255,0.1));
  }
  .da-timeline-nodes {
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: 0; position: relative; z-index: 1;
  }
  .da-tnode { display: flex; flex-direction: column; align-items: flex-start; padding-right: 20px; }
  .da-tnode-dot {
    width: 32px; height: 32px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.2);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 16px; background: #000; position: relative; z-index: 2;
    transition: border-color 0.3s;
  }
  .da-tnode-dot::after {
    content: ''; width: 8px; height: 8px; border-radius: 50%;
    background: rgba(255,255,255,0.2);
  }
  .da-tnode--active .da-tnode-dot {
    border-color: #fff;
    background: #fff;
  }
  .da-tnode--active .da-tnode-dot::after { background: #000; }
  .da-tnode-day {
    font-size: 8px; font-weight: 700; letter-spacing: 0.4em;
    text-transform: uppercase; color: rgba(255,255,255,0.3); margin-bottom: 6px;
  }
  .da-tnode--active .da-tnode-day { color: #fff; }
  .da-tnode-label {
    font-size: 13px; font-weight: 300; letter-spacing: 0.06em;
    text-transform: uppercase; color: rgba(255,255,255,0.6);
    line-height: 1.3; white-space: pre-line; margin-bottom: 8px;
  }
  .da-tnode-sub {
    font-size: 10px; font-weight: 300; letter-spacing: 0.04em;
    color: rgba(255,255,255,0.25); line-height: 1.5; white-space: pre-line;
  }

  /* ─────────────────────────────────────────────────────
     DATA SPLIT GRAPHIC
  ───────────────────────────────────────────────────── */
  .da-split-graphic {
    display: flex; align-items: center; justify-content: center;
    gap: 32px; margin-bottom: 56px; padding: 0 0 32px;
    border-bottom: 1px solid rgba(0,0,0,0.08);
  }
  .da-split-col { display: flex; flex-direction: column; align-items: center; gap: 12px; width: 120px; }
  .da-split-icon {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; width: 72px;
  }
  .da-split-block {
    height: 18px; border-radius: 1px;
    transition: opacity 0.3s;
  }
  .da-split-block--erased { background: rgba(0,0,0,0.12); }
  .da-split-block--erased.da-split-block--0,
  .da-split-block--erased.da-split-block--2,
  .da-split-block--erased.da-split-block--4 { opacity: 0.3; }
  .da-split-block--retained { background: #000; }
  .da-split-block--retained.da-split-block--1,
  .da-split-block--retained.da-split-block--3 { opacity: 0.5; }
  .da-split-col-label {
    font-size: 8px; font-weight: 600; letter-spacing: 0.4em;
    text-transform: uppercase; color: rgba(0,0,0,0.4);
  }
  .da-split-divider {
    display: flex; flex-direction: column; align-items: center; gap: 8px;
  }
  .da-split-divider-line { width: 1px; height: 28px; background: rgba(0,0,0,0.1); }
  .da-split-divider-circle {
    width: 32px; height: 32px; border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.12);
    display: flex; align-items: center; justify-content: center;
    font-size: 7px; font-weight: 700; letter-spacing: 0.1em; color: rgba(0,0,0,0.3);
  }

  /* ── Data grid ── */
  .da-data-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 2px;
  }
  .da-data-col { padding: 32px; box-sizing: border-box; }
  .da-data-col--erased  { background: rgba(0,0,0,0.03); }
  .da-data-col--retained { background: #000; }
  .da-data-col-header { margin-bottom: 24px; }
  .da-data-col-badge {
    display: inline-block; font-size: 7px; font-weight: 600;
    letter-spacing: 0.4em; text-transform: uppercase;
    padding: 4px 10px; margin-bottom: 12px;
  }
  .da-data-col-badge--erased  { border: 1px solid rgba(0,0,0,0.15); color: rgba(0,0,0,0.4); }
  .da-data-col-badge--retained { border: 1px solid rgba(255,255,255,0.15); color: rgba(255,255,255,0.5); }
  .da-data-col-title {
    font-size: 18px; font-weight: 300; letter-spacing: 0.1em;
    text-transform: uppercase; margin: 0 0 8px;
  }
  .da-data-col--erased  .da-data-col-title { color: #000; }
  .da-data-col--retained .da-data-col-title { color: #fff; }
  .da-data-col-sub {
    font-size: 10px; font-weight: 300; letter-spacing: 0.04em; line-height: 1.6; margin: 0;
  }
  .da-data-col--erased  .da-data-col-sub { color: rgba(0,0,0,0.4); }
  .da-data-col--retained .da-data-col-sub { color: rgba(255,255,255,0.35); }
  .da-data-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0; }
  .da-data-item {
    display: flex; align-items: center; gap: 12px;
    padding: 11px 0;
    font-size: 11px; font-weight: 300; letter-spacing: 0.08em;
    border-bottom: 1px solid;
  }
  .da-data-item:last-child { border-bottom: none; }
  .da-data-item--erased   { color: rgba(0,0,0,0.5); border-color: rgba(0,0,0,0.06); }
  .da-data-item--retained { color: rgba(255,255,255,0.6); border-color: rgba(255,255,255,0.06); }
  .da-data-item-icon {
    font-size: 8px; flex-shrink: 0; opacity: 0.4;
  }
  .da-data-col-note {
    display: flex; gap: 12px; margin-top: 20px;
    padding: 14px 16px; border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.04);
  }
  .da-data-col-note-icon {
    font-size: 16px; font-weight: 700; color: rgba(255,255,255,0.2); flex-shrink: 0; line-height: 1;
  }
  .da-data-col-note p {
    font-size: 9px; font-weight: 300; letter-spacing: 0.04em;
    color: rgba(255,255,255,0.3); line-height: 1.7; margin: 0;
  }

  /* ─────────────────────────────────────────────────────
     VAULT GRAPHIC (CSS)
  ───────────────────────────────────────────────────── */
  .da-vault {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    margin-top: 48px;
  }
  .da-vault-body {
    width: 100px; height: 100px; position: relative;
    display: flex; align-items: center; justify-content: center;
  }
  .da-vault-ring {
    position: absolute; border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.12);
  }
  .da-vault-ring--1 { width: 100px; height: 100px; }
  .da-vault-ring--2 { width: 72px;  height: 72px;  border-color: rgba(0,0,0,0.08); }
  .da-vault-ring--3 { width: 48px;  height: 48px;  border-color: rgba(0,0,0,0.15); }
  .da-vault-core {
    width: 24px; height: 24px;
    border: 1px solid #000;
    display: flex; align-items: center; justify-content: center;
    position: relative;
  }
  .da-vault-line {
    position: absolute; background: #000;
  }
  .da-vault-line--h { width: 12px; height: 1px; }
  .da-vault-line--v { width: 1px; height: 12px; }
  .da-vault-dot {
    width: 4px; height: 4px; border-radius: 50%; background: #000; position: absolute;
  }
  .da-vault-label {
    font-size: 7px; font-weight: 600; letter-spacing: 0.45em;
    text-transform: uppercase; color: rgba(0,0,0,0.25);
  }

  /* ── Vault layout ── */
  .da-vault-layout {
    display: grid; grid-template-columns: 1fr 1.5fr; gap: 80px; align-items: start;
  }
  .da-vault-left {}
  .da-vault-right { display: flex; flex-direction: column; gap: 0; }
  .da-commitment {
    display: flex; gap: 20px;
    padding: 20px 0; border-bottom: 1px solid rgba(0,0,0,0.08);
    animation: da-fade-in 0.5s ease both;
  }
  .da-commitment:last-child { border-bottom: none; }
  @keyframes da-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .da-commitment-num {
    font-size: 9px; font-weight: 700; letter-spacing: 0.3em;
    color: rgba(0,0,0,0.2); flex-shrink: 0; padding-top: 2px; width: 24px;
  }
  .da-commitment-title {
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em;
    text-transform: uppercase; color: #000; margin: 0 0 6px;
  }
  .da-commitment-desc {
    font-size: 11px; font-weight: 300; letter-spacing: 0.04em;
    color: rgba(0,0,0,0.5); line-height: 1.7; margin: 0;
  }

  /* ─────────────────────────────────────────────────────
     RECOVERY SECTION
  ───────────────────────────────────────────────────── */
  .da-recovery-block {
    display: flex; gap: 64px; align-items: flex-start;
  }
  .da-recovery-number {
    font-size: clamp(80px, 12vw, 160px); font-weight: 900;
    letter-spacing: -0.04em; color: rgba(255,255,255,0.06);
    line-height: 0.8; flex-shrink: 0; user-select: none;
  }
  .da-recovery-content { flex: 1; }
  .da-recovery-cta {
    display: inline-block; margin-top: 28px;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 600; letter-spacing: 0.4em;
    text-transform: uppercase; color: #fff;
    text-decoration: none;
    border-bottom: 1px solid rgba(255,255,255,0.3);
    padding-bottom: 2px;
    transition: border-color 0.2s;
  }
  .da-recovery-cta:hover { border-color: #fff; }

  /* ─────────────────────────────────────────────────────
     FAQ
  ───────────────────────────────────────────────────── */
  .da-faq-list { display: flex; flex-direction: column; }
  .da-faq-item { border-bottom: 1px solid rgba(0,0,0,0.08); }
  .da-faq-q {
    width: 100%; background: none; border: none; cursor: pointer;
    display: flex; justify-content: space-between; align-items: center; gap: 24px;
    padding: 20px 0; text-align: left;
    font-family: 'Exo 2', sans-serif;
    font-size: 12px; font-weight: 400; letter-spacing: 0.06em; color: #000;
    transition: color 0.2s;
  }
  .da-faq-q:hover { color: rgba(0,0,0,0.6); }
  .da-faq-icon {
    font-size: 18px; font-weight: 200; flex-shrink: 0;
    color: rgba(0,0,0,0.3); transition: transform 0.2s;
  }
  .da-faq-item--open .da-faq-icon { transform: rotate(180deg); }
  .da-faq-a {
    overflow: hidden; transition: max-height 0.35s ease;
  }
  .da-faq-a p {
    padding: 0 0 20px;
    font-size: 12px; font-weight: 300; letter-spacing: 0.04em;
    color: rgba(0,0,0,0.5); line-height: 1.8; margin: 0;
  }

  /* ─────────────────────────────────────────────────────
     CTA SECTION
  ───────────────────────────────────────────────────── */
  .da-cta-block { position: relative; padding: 32px 0; }
  .da-cta-lines {
    position: absolute; top: 0; right: 0; width: 200px; height: 100%;
    pointer-events: none; overflow: hidden;
  }
  .da-cta-line {
    position: absolute; background: rgba(0,0,0,0.06);
    width: 1px; height: 100%; right: 60px;
  }
  .da-cta-line--2 { right: 120px; }
  .da-cta-actions {
    display: flex; align-items: center; gap: 16px;
    margin-top: 32px; flex-wrap: wrap;
  }
  .da-cta-btn-primary {
    background: #000; color: #fff; border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 9px; font-weight: 600; letter-spacing: 0.4em;
    text-transform: uppercase; padding: 16px 32px;
    cursor: pointer; transition: background 0.2s;
  }
  .da-cta-btn-primary:hover { background: #222; }
  .da-cta-btn-secondary {
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 500; letter-spacing: 0.35em;
    text-transform: uppercase; color: rgba(0,0,0,0.4);
    text-decoration: none;
    border-bottom: 1px solid rgba(0,0,0,0.15); padding-bottom: 1px;
    transition: color 0.2s, border-color 0.2s;
  }
  .da-cta-btn-secondary:hover { color: #000; border-color: #000; }
  .da-cta-note {
    margin-top: 16px; font-size: 10px; font-weight: 300;
    letter-spacing: 0.08em; color: rgba(0,0,0,0.35);
  }

  /* ─────────────────────────────────────────────────────
     RESPONSIVE
  ───────────────────────────────────────────────────── */
  @media (max-width: 1024px) {
    .da-section { padding: 72px 48px; }
    .da-hero { padding: 72px 48px; }
    .da-hero-scroll-hint { left: 48px; }
    .da-hero-graphic { right: 48px; width: 220px; height: 220px; }
    .da-hero-circle--1 { width: 220px; height: 220px; }
    .da-hero-circle--2 { width: 160px; height: 160px; }
    .da-hero-circle--3 { width: 100px; height: 100px; }
    .da-vault-layout { grid-template-columns: 1fr; gap: 48px; }
  }
  @media (max-width: 768px) {
    .da-section { padding: 56px 24px; }
    .da-hero { padding: 80px 24px 120px; min-height: auto; }
    .da-hero-graphic { display: none; }
    .da-hero-scroll-hint { left: 24px; }
    .da-timeline-nodes { grid-template-columns: 1fr 1fr; gap: 32px; }
    .da-data-grid { grid-template-columns: 1fr; }
    .da-recovery-block { flex-direction: column; gap: 24px; }
    .da-recovery-number { font-size: 80px; }
    .da-split-graphic { flex-direction: column; gap: 16px; }
    .da-split-divider { flex-direction: row; }
    .da-split-divider-line { width: 28px; height: 1px; }
  }
  @media (max-width: 480px) {
    .da-section { padding: 48px 20px; }
    .da-hero { padding: 72px 20px 100px; }
    .da-timeline-nodes { grid-template-columns: 1fr; gap: 24px; }
    .da-timeline-track { display: none; }
    .da-data-col { padding: 24px 20px; }
    .da-cta-actions { flex-direction: column; align-items: flex-start; }
    .da-cta-btn-primary { width: 100%; text-align: center; }
  }
`;

export default DeleteAccountPage;
